// famewall-widget — the per-instance ESM surface of the no-iframe embed
// (built to public/widget/famewall-widget.js by scripts/build-seo-embed.mjs).
//
// Same renderer, same shims, same CSS as newFrame.js; a different loading
// contract. newFrame.js is a page-level bootstrap: scan .famewall-embed once
// on DOM ready — fine for classic sites, wrong for component lifecycles and
// client-side routing. This module is for integrations that own a lifecycle
// (the Framer code component first, the Wix custom element next):
//
//   const widget = await import('https://embed.famewall.io/widget/famewall-widget.js');
//   widget.mount(element, { wall: 'acme', layout: 'wall', theme: 'dark' });
//   widget.unmount(element);
//
// layout: 'wall' | 'carousel' | 'slider' ('grid' and 'avatars' accepted too).
// It also registers <famewall-embed wall="acme" layout="carousel"> for
// script-free markup usage; connected/disconnected callbacks map straight to
// mount/unmount, so SPA route changes just work.

import {
  normalizeOptions,
  hasRequiredIdentifier,
} from './lib/embedConfig';
import { renderEmbed, teardownEmbed } from './lib/renderEmbed';
import { setEmbedOrigin } from './lib/origin';

const DEFAULT_ORIGIN = 'https://embed.famewall.io';

// import.meta.url is the usual self-location for an ES module
// (document.currentScript is null inside modules). The origin doubles as the
// API origin (/api/seo-embed) and the asset origin (/DarkFamewallLogo.svg...),
// so a locally served build automatically talks to the local stack.
//
// ...but it is NOT reliable everywhere. A host that shims dynamic import()
// — Framer's canvas and preview run modules through es-module-shims —
// executes the module from a blob: URL, and `new URL('blob:https://host/id')
// .origin` is the HOST's origin, not ours. The module then calls
// <framercanvas>/api/seo-embed and the fetch fails ("Failed to fetch") even
// though the module itself loaded fine. Guard: only trust an http(s) module
// URL, and let callers that already know the host pass it explicitly.
function resolveModuleOrigin() {
  try {
    const url = new URL(import.meta.url);

    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.origin;
    }
  } catch (err) {
    // Fall through to the default.
  }

  return DEFAULT_ORIGIN;
}

const MODULE_ORIGIN = resolveModuleOrigin();
setEmbedOrigin(MODULE_ORIGIN);

// An explicit `origin` beats anything derived: the caller loaded this module
// from a URL it chose, so it knows the host for certain. Integrations should
// always pass it — it is the only thing that survives a sandbox that rewrites
// import.meta.url.
function resolveOrigin(options) {
  const explicit = options && (options.origin || options.apiOrigin);

  if (typeof explicit !== 'string' || !explicit.trim()) {
    return MODULE_ORIGIN;
  }

  const origin = explicit.trim().replace(/\/+$/, '');

  // Keep the asset resolver in step: the card components build root-relative
  // image URLs off the same origin.
  setEmbedOrigin(origin);

  return origin;
}

export function mount(element, options) {
  if (typeof window === 'undefined' || !element) {
    return false;
  }

  const config = normalizeOptions(options);

  if (!hasRequiredIdentifier(config)) {
    return false;
  }

  return renderEmbed(element, config, resolveOrigin(options));
}

export function unmount(element) {
  return teardownEmbed(element);
}

// The same API on a window global, for consumers that load this module via a
// <script type="module"> TAG instead of dynamic import() — module scripts
// can't hand exports to the page. The Framer component uses this as its
// fallback loading mechanism (some sandboxes/bundlers refuse a runtime
// import() but happily run an injected module tag).
if (typeof window !== 'undefined') {
  const existing = window.FamewallWidget;
  if (!existing || typeof existing.mount !== 'function') {
    window.FamewallWidget = { mount: mount, unmount: unmount };
  }
}

// --- <famewall-embed> custom element -----------------------------------

// Attributes accepted with or without a data- prefix, mirroring the mount()
// options: wall/src, layout/format, theme, autoplay, spacing, limit, debug,
// collection.
function attributeOptions(element) {
  const attr = (name) =>
    element.getAttribute(name) || element.getAttribute('data-' + name) || '';

  return {
    wall: attr('wall') || attr('src') || attr('wall-url'),
    layout: attr('layout') || attr('format'),
    theme: attr('theme'),
    autoplay: attr('autoplay'),
    spacing: attr('spacing'),
    limit: attr('limit'),
    debug: attr('debug'),
    collection: attr('collection') || attr('collection-id'),
    // Same escape hatch as mount(): a host that rewrites import.meta.url can
    // be corrected with <famewall-embed origin="https://embed.famewall.io">.
    origin: attr('origin'),
  };
}

const OBSERVED_BASE = [
  'wall',
  'src',
  'wall-url',
  'layout',
  'format',
  'theme',
  'autoplay',
  'spacing',
  'limit',
  'collection',
  'collection-id',
  'origin',
];

function defineCustomElement() {
  if (
    typeof window === 'undefined' ||
    typeof HTMLElement === 'undefined' ||
    !window.customElements ||
    window.customElements.get('famewall-embed')
  ) {
    return;
  }

  class FamewallEmbedElement extends HTMLElement {
    static get observedAttributes() {
      return OBSERVED_BASE.concat(OBSERVED_BASE.map((name) => 'data-' + name));
    }

    connectedCallback() {
      // Custom elements are display:inline by default, which collapses a
      // block widget. Only set it when the page hasn't chosen a display.
      if (!this.style.display) {
        this.style.display = 'block';
      }
      this._famewallScheduleMount();
    }

    disconnectedCallback() {
      teardownEmbed(this);
    }

    attributeChangedCallback() {
      if (this.isConnected) {
        this._famewallScheduleMount();
      }
    }

    // Coalesce connected + N attribute callbacks into one mount per microtask
    // (initial parse fires attributeChangedCallback once per attribute).
    _famewallScheduleMount() {
      if (this._famewallMountQueued) {
        return;
      }
      this._famewallMountQueued = true;

      Promise.resolve().then(() => {
        this._famewallMountQueued = false;
        if (this.isConnected) {
          mount(this, attributeOptions(this));
        }
      });
    }
  }

  window.customElements.define('famewall-embed', FamewallEmbedElement);
}

defineCustomElement();
